import api from '../axiosConfig';
import { REPORTS } from '../config/apiEndpoints';

const ReportService = {
    /**
     * Get the master Reports Dashboard overview metrics.
     * @param {string} tenantId - The active global session context tenant ID
     * @returns {Promise<Object>} Dashboard metrics data payload
     */
    getDashboard: async (tenantId) => {
        const response = await api.get(REPORTS.DASHBOARD, {
            headers: {
                'x-tenant-id': tenantId
            }
        });
        return response.data;
    },
    
    /**
     * Get the Occupancy Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, period, roomTypeId, noPaging, etc.)
     * @returns {Promise<Object>} Occupancy report payload
     */
    getOccupancyReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.OCCUPANCY, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Occupancy Report data in the requested format.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters including format (excel, csv, pdf)
     * @returns {Promise<Blob>} Occupancy report exported file blob
     */
    exportOccupancyReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.OCCUPANCY, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Arrivals & Departures Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (date, noPaging, etc.)
     * @returns {Promise<Object>} Arrivals & Departures report payload
     */
    getArrivalsDeparturesReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.ARRIVALS_DEPARTURES, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Arrivals & Departures Report data in the requested format.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters including format (excel, csv, pdf)
     * @returns {Promise<Blob>} Arrivals & Departures report exported file blob
     */
    exportArrivalsDeparturesReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.ARRIVALS_DEPARTURES, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Reservation Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, period, status, bookingSource, roomTypeId, noPaging)
     * @returns {Promise<Object>} Reservation report payload
     */
    getReservationReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.RESERVATIONS, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Reservation Report data in the requested format.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters including format (excel, csv, pdf)
     * @returns {Promise<Blob>} Reservation report exported file blob
     */
    exportReservationReport: async (tenantId, params = {}) => {
        // If there's a specific /export endpoint we can use it, but keeping it standard for now.
        const response = await api.get(REPORTS.RESERVATIONS, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Cancellation & No-Show Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, period, bookingSource, noPaging)
     * @returns {Promise<Object>} Cancellation report payload
     */
    getCancellationReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.CANCELLATIONS, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Cancellation & No-Show Report data in the requested format.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters including format (excel, csv, pdf)
     * @returns {Promise<Blob>} Cancellation report exported file blob
     */
    exportCancellationReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.CANCELLATIONS, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Housekeeping Performance Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Housekeeping report payload
     */
    getHousekeepingReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.HOUSEKEEPING, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Housekeeping Performance Report data.
     */
    exportHousekeepingReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.HOUSEKEEPING, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Restaurant Revenue Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, period, mealCategory)
     * @returns {Promise<Object>} Restaurant Revenue report payload
     */
    getRestaurantRevenueReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.RESTAURANT_REVENUE, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the POS / Restaurant Revenue Report data.
     */
    exportRestaurantRevenueReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.RESTAURANT_REVENUE, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Financial P&L Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, period)
     * @returns {Promise<Object>} P&L report payload
     */
    getPnlReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.PNL, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Financial P&L Report data.
     */
    exportPnlReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.PNL, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Inventory Stock Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, period, departmentId, categoryId)
     * @returns {Promise<Object>} Inventory report payload
     */
    getInventoryReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.INVENTORY, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Inventory Usage Report data.
     */
    exportInventoryReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.INVENTORY, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Staff Attendance Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, period, departmentId)
     * @returns {Promise<Object>} Staff Attendance report payload
     */
    getStaffAttendanceReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.STAFF_ATTENDANCE, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Staff Attendance Report data.
     */
    exportStaffAttendanceReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.STAFF_ATTENDANCE, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Get the Rate Variance / Overrides Report data.
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} params - Query parameters (startDate, endDate, userId, reasonCategory, etc.)
     * @returns {Promise<Object>} Rate Variance report payload
     */
    getRateVarianceReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.RATE_VARIANCE, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Export the Rate Variance Report data.
     */
    exportRateVarianceReport: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.RATE_VARIANCE, {
            headers: {
                'x-tenant-id': tenantId
            },
            params,
            responseType: 'blob'
        });
        return response;
    },
    
    /**
     * Schedule a new Automated Report
     * @param {string} tenantId - The active global session context tenant ID
     * @param {Object} payload - Configuration payload (reportType, frequency, deliveryTime, recipients, format)
     * @returns {Promise<Object>} Creation success payload
     */
    scheduleReport: async (tenantId, payload) => {
        const response = await api.post(REPORTS.SCHEDULED, payload, {
            headers: {
                'x-tenant-id': tenantId
            }
        });
        return response.data;
    },
    
    /**
     * Get Scheduled Reports List
     * @param {string} tenantId - The active global session context tenant ID
     * @returns {Promise<Object>} Scheduled reports payload
     */
    getScheduledReports: async (tenantId, params = {}) => {
        const response = await api.get(REPORTS.SCHEDULED, {
            headers: {
                'x-tenant-id': tenantId
            },
            params
        });
        return response.data;
    },
    
    /**
     * Update an existing Scheduled Report
     * @param {string} tenantId - The active global session context tenant ID
     * @param {string} id - The ID of the scheduled report to update
     * @param {Object} payload - Update payload (reportType, frequency, deliveryTime, recipients, format, status)
     * @returns {Promise<Object>} Update success payload
     */
    updateScheduledReport: async (tenantId, id, payload) => {
        const response = await api.put(`${REPORTS.SCHEDULED}/${id}`, payload, {
            headers: {
                'x-tenant-id': tenantId
            }
        });
        return response.data;
    },
    
    /**
     * Delete a Scheduled Report
     * @param {string} tenantId - The active global session context tenant ID
     * @param {string} id - The ID of the scheduled report to delete
     * @returns {Promise<Object>} Deletion success payload
     */
    deleteScheduledReport: async (tenantId, id) => {
        const response = await api.delete(`${REPORTS.SCHEDULED}/${id}`, {
            headers: {
                'x-tenant-id': tenantId
            }
        });
        return response.data;
    }
};

export default ReportService;
